import equal from 'fast-deep-equal'
import { useEffect, useState } from 'react'
import { combineLatest, map } from 'rxjs'
import { getResourceObservable } from '../storage'
import getCollection from '../support/getCollection'
import type { ListParams, ResourceType } from '../types'

/**
 * Use a list of resource ids based on the parameters provided. Suspend until the API replies.
 */
export function useCollectionIds<K extends ResourceType>(type: K, ...params: ListParams<K>): string[] {
  return getCollection(type, ...params).useKeys()
}

/**
 * Use a list of resource ids based on the parameters provided. Suspend until the API replies.
 */

const observableFilterInit = (ids: string[], filterPredicate: any) => {
  const observables = ids.map((id) => getResourceObservable(id)!)
  const objectObservables = Object.values(observables)
  let filterIds: string[] = []

  const sub = combineLatest(objectObservables)
    .pipe(map((objects) => objects.filter(filterPredicate).map((obj) => obj.id)))
    .subscribe((filteredIds) => {
      filterIds = [...filteredIds]
    })

  sub.unsubscribe()
  return filterIds
}

/**
 * Use a list of resource ids based on the parameters provided AND a filter predicate. Suspend until the API replies.
 */
export function useFilteredCollection<K extends ResourceType>(
  type: K,
  filterPredicate: any,
  ...params: ListParams<K>
): string[] {
  const ids = getCollection(type, ...params).useKeys()
  const [filteredIDs, setFilteredIDs] = useState<any[]>(() => observableFilterInit(ids, filterPredicate))

  useEffect(() => {
    const observables = ids.map((id) => getResourceObservable<K>(id)!)
    const objectObservables = Object.values(observables)

    const subscription = combineLatest(objectObservables)
      .pipe(map((objects) => objects.filter(filterPredicate).map((obj) => obj.id)))
      .subscribe((filteredIds) => {
        if (!equal(filteredIDs, filteredIds)) {
          setFilteredIDs(filteredIds)
        }
      })

    return () => {
      subscription.unsubscribe()
    }
  }, [filterPredicate, ids])

  return filteredIDs
}
